import { ChangeDetectorRef, Component, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import moment from 'moment';
import { DateRangePickerComponent } from '../date-range-picker/date-range-picker.component';

const YEARS_PER_PAGE = 24;

@Component({
    selector: 'ceres-date-range-picker-fy-header',
    templateUrl: './date-range-picker-fy-header.component.html',
    styleUrls: ['./date-range-picker-fy-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DateRangePickerFyHeaderComponent implements OnDestroy {
    readonly cdr = inject(ChangeDetectorRef);
    private readonly calendar = inject<MatCalendar<moment.Moment>>(MatCalendar);
    private readonly dateAdapter = inject<DateAdapter<moment.Moment>>(DateAdapter);
    private readonly dateRangePicker = inject<DateRangePickerComponent<moment.Moment>>(DateRangePickerComponent);
    private _dateFormats = inject<MatDateFormats>(MAT_DATE_FORMATS);

    private readonly subscriptions = new Subscription();

    // shown in the FY dropdown's placeholder when the picked range doesn't line up
    // with a fiscal year (free/custom range). Empty while a real FY is selected.
    public selectionLabel = '';

    public get currentIndex(): number {
        return this.fiscalYears.indexOf(this.fiscalYearCtrl.value);
    }

    public get isFirstIndex(): boolean {
        // currentIndex is -1 for a custom range → treat as "at the start" so only "next" is offered.
        return this.currentIndex <= 0;
    }

    public get isLastIndex(): boolean {
        return this.currentIndex === this.fiscalYears.length - 1;
    }

    public get isPreviousDisabled(): boolean {
        // Only clamp in month view; year/multi-year navigation stays open (min is far in the past).
        return this.calendar.currentView === 'month' && this.calendar.activeDate.isSame(this.calendar.minDate, 'month');
    }

    public get isNextDisabled(): boolean {
        return this.calendar.currentView === 'month' && this.calendar.activeDate.isSame(this.calendar.maxDate, 'month');
    }

    public get isMonthView(): boolean {
        return this.calendar.currentView === 'month';
    }

    public get fiscalYearCtrl(): FormControl {
        return this.dateRangePicker.fiscalYearCtrl;
    }

    public get fiscalYears(): {
        date: Date;
        dateEnd: Date;
        label: string;
    }[] {
        return this.dateRangePicker.fiscalYears;
    }

    get periodLabel() {
        if (this.calendar.currentView === 'month') {
            return this.dateAdapter
                .format(this.calendar.activeDate, this._dateFormats.display.monthYearLabel)
                .toLocaleUpperCase();
        }

        return this.dateAdapter.getYearName(this.calendar.activeDate);
    }

    public constructor() {
        this.subscriptions.add(this.calendar.stateChanges.pipe(tap(() => this.cdr.markForCheck())).subscribe());

        this.subscriptions.add(
            this.fiscalYearCtrl.valueChanges
                .pipe(
                    filter((fiscalYear) => !!fiscalYear),
                    distinctUntilChanged(),
                    tap((fiscalYear) => this.onFiscalYearSelected(fiscalYear))
                )
                .subscribe()
        );

        this.updateCalendar(this.fiscalYearCtrl.value);

        // Keep the dropdown in sync with what's actually selected on the calendar: snap to a
        // fiscal year when the range matches one, otherwise show the custom range as a placeholder.
        this.subscriptions.add(
            this.dateRangePicker.range.valueChanges
                .pipe(
                    distinctUntilChanged(),
                    tap(() => this.syncSelectionFromRange())
                )
                .subscribe()
        );
        this.syncSelectionFromRange();
    }

    public setNextFiscalYear(): void {
        if (this.isLastIndex) {
            throw new Error('There is further fiscal year selectable');
        }

        this.fiscalYearCtrl.setValue(this.fiscalYears[this.currentIndex + 1]);
    }

    public setPreviousFiscalYear(): void {
        if (this.isFirstIndex) {
            throw new Error('There is no previous fiscal year selectable');
        }

        this.fiscalYearCtrl.setValue(this.fiscalYears[this.currentIndex - 1]);
    }

    /** Toggle between the month grid and the multi-year grid, like the default calendar header. */
    public currentPeriodClicked(): void {
        this.calendar.currentView = this.calendar.currentView === 'month' ? 'multi-year' : 'month';
    }

    public setNextPeriod(): void {
        this.calendar.activeDate =
            this.calendar.currentView === 'month'
                ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1)
                : this.dateAdapter.addCalendarYears(
                      this.calendar.activeDate,
                      this.calendar.currentView === 'year' ? 1 : YEARS_PER_PAGE
                  );
    }

    public setPreviousPeriod(): void {
        this.calendar.activeDate =
            this.calendar.currentView === 'month'
                ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1)
                : this.dateAdapter.addCalendarYears(
                      this.calendar.activeDate,
                      this.calendar.currentView === 'year' ? -1 : -YEARS_PER_PAGE
                  );
    }

    private syncSelectionFromRange(): void {
        const { start, end } = this.dateRangePicker.range.value;
        if (!start) {
            this.fiscalYearCtrl.setValue(null, { emitEvent: false });
            this.selectionLabel = '';
            this.cdr.markForCheck();
            return;
        }

        // Mid-selection: only the start is picked so far. Mirror the input (which shows the start
        // only) instead of pairing it with the previous, now-stale end.
        if (!end) {
            this.fiscalYearCtrl.setValue(null, { emitEvent: false });
            this.selectionLabel = `${moment(start).format('DD.MM.YYYY')} \u2013 \u2026`;
            this.cdr.markForCheck();
            return;
        }

        const matchingFiscalYear = this.fiscalYears.find(
            (fy) => moment(start).isSame(moment(fy.date), 'day') && moment(end).isSame(moment(fy.dateEnd), 'day')
        );

        // emitEvent: false so we don't loop back through the FY-change handler (which resets the range).
        this.fiscalYearCtrl.setValue(matchingFiscalYear ?? null, { emitEvent: false });
        this.selectionLabel = matchingFiscalYear
            ? ''
            : `${moment(start).format('DD.MM.YYYY')} \u2013 ${moment(end).format('DD.MM.YYYY')}`;
        this.cdr.markForCheck();
    }

    private updateCalendar(fiscalYear?: { date: Date; dateEnd: Date } | null): void {
        // FiscalYear mode doubles as Dynamic. The FY dropdown only presets/navigates;
        // the calendar itself isn't clamped to a single FY, so users can free-select ranges
        // spanning many fiscal years. Past is effectively unlimited (deeper project history),
        // future stays capped at the last dropdown FY. Ceiling: 50y history floor — widen
        // HISTORY_YEARS if deeper history is ever needed.
        const HISTORY_YEARS = 50;
        this.calendar.minDate = moment(this.fiscalYears[0].date).subtract(HISTORY_YEARS, 'years');
        this.calendar.maxDate = moment(this.fiscalYears[this.fiscalYears.length - 1].dateEnd);

        // No FY selected (custom range reopened) → land on the current range start, then first FY.
        const navigateTo = fiscalYear?.date ?? this.dateRangePicker.range.value?.start ?? this.fiscalYears[0].date;

        setTimeout(() => {
            this.calendar.activeDate = moment(navigateTo);
        }, 0);
    }

    private onFiscalYearSelected(fiscalYear: { date: Date; dateEnd: Date }): void {
        this.updateCalendar(fiscalYear);
        this.selectMinAndMaxRange(fiscalYear);
    }

    private selectMinAndMaxRange({ date, dateEnd }: { date: Date; dateEnd: Date }): void {
        const minDate = moment(date);
        const maxDate = moment(dateEnd);

        this.dateRangePicker.range.setValue({
            start: minDate,
            end: maxDate
        });
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
